import SearchBar, { NoResults } from 'live/components/ui/SearchBar'
import { Sidebar } from '@intelie/ui'
import React, { useContext, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { getAssetLink } from '../common/utils'
import { AssetTree } from './assetTree'
import i18n from 'live/services/i18n'
import Scrollbars from 'react-scrollbars-custom'
import scrollbarStyles from '../common/scrollbarStyles'
import { AssetTreeContext, ParamsContext } from '../qualityMonitoring/qualityMonitoring'
import useOnClickOutside from './useClickOutside'

export const COLAPSED_NAV_WIDTH = 50

export function AssetNav(): JSX.Element {
    const [filter, setFilter] = useState('')
    const [searchresults, setSearchResults] = useState([])
    const ref = useRef(null)
    const assetTree = useContext(AssetTreeContext)
    const urlParams = useContext(ParamsContext).split('/')

    useEffect(() => {
        if (filter) {
            const newResults = searchInConfigs(filter, assetTree)
            setSearchResults(newResults)
        } else {
            setSearchResults([])
        }
    }, [filter, assetTree])

    function handleClickOutside(): void {
        const navButton = document.querySelectorAll('[class^=Sidebar__StyledSidebarButtonDiv]')[0] as HTMLElement
        const style = window.getComputedStyle(navButton)
        const transform = style.getPropertyValue('transform')
        if (transform === 'none') navButton.click()
    }

    useOnClickOutside(ref, handleClickOutside)

    return (
        <StyledAssetNav className="assetNav" ref={ref}>
            <Sidebar
                fullWidth="300px"
                collapsedWidth={`${COLAPSED_NAV_WIDTH}px`}
                startCollapsed={true}
                headerTitle={i18n('Factory List')}
                collapsedChildren={<div className={'assetNav--collapsedNavText'}>{i18n('Factory List')}</div>}
                backgroundColorGray
            >
                <div className="assetNav--sideBarContent">
                    <SearchBar filter={filter} setFilter={setFilter} />
                    <div className={'assetNav--sideBarContent__scrollContainer'}>
                        <Scrollbars
                            thumbYProps={scrollbarStyles.thumbYProps}
                            trackYProps={{ style: { ...scrollbarStyles.trackYProps.style, right: -14 } }}
                            disableTrackYWidthCompensation
                            removeTracksWhenNotUsed
                            mobileNative
                            noScrollX
                        >
                            <NoResults
                                show={!!filter && !searchresults.length}
                                searchLoading={false}
                                clearFilters={() => setFilter('')}
                            />
                            {!searchresults.length && !filter ? (
                                <AssetTree />
                            ) : (
                                <ul className={'assetNav--sideBarContent__scrollContainer--resultsList'}>
                                    {searchresults.map(result => (
                                        <li
                                            key={result}
                                            className={
                                                'assetNav--sideBarContent__scrollContainer--resultsList__listItem'
                                            }
                                        >
                                            <a
                                                href={getAssetLink(`${urlParams[0]}/${result}`)}
                                                className={
                                                    'assetNav--sideBarContent__scrollContainer--resultsList__listItem--searchResult'
                                                }
                                                onClick={() => {
                                                    setFilter('')
                                                    setSearchResults([])
                                                }}
                                            >
                                                {result}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </Scrollbars>
                    </div>
                </div>
            </Sidebar>
        </StyledAssetNav>
    )
}

const liveHeaderHeight = 35

const StyledAssetNav = styled.div`
    --headersDiscount: ${liveHeaderHeight}px;
    display: flex;
    height: calc(100vh - var(--headersDiscount));
    position: fixed;
    top: var(--headersDiscount);
    left: 0;
    z-index: 3;
    .assetNav {
        &--collapsedNavText {
            font-size: 16px;
            white-space: nowrap;
        }
        &--sideBarContent {
            padding: 16px;
            height: 90%;
            &__scrollContainer {
                height: 87vh;
                &--resultsList {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: start;
                    margin-top: 12px;
                    &__listItem {
                        margin-bottom: 8px;
                        &--searchResult {
                            text-decoration: none;
                            font-size: 14px;
                        }
                    }
                }
            }
        }
    }
`

/**
 * This function iterates through every level of the tree configs to find branches that match the search input value
 *
 * @param searchString Value that was input in the side nav search field
 * @returns an array containing the path to all the branches that contain the string that was input
 */
export function searchInConfigs(searchString: string, assetTree: CountryData[]): string[] {
    const pathsArray = []

    function containsString(name: string): boolean {
        return name.toLowerCase().includes(searchString.toLowerCase())
    }

    // Check within countries
    assetTree.forEach(country => {
        const { countryName } = country
        // Check if countries name match the string
        // if it does, add path to result
        if (containsString(countryName)) pathsArray.push(`${countryName}`)

        // Check within factories
        country.factories.forEach(factory => {
            const { factoryName } = factory
            // Check if factories name match the string
            // if it does, add path to result
            if (containsString(factoryName)) pathsArray.push(`${countryName}/${factoryName}`)

            // Check within lines
            factory.lines.forEach(line => {
                const { lineName } = line
                // Check if lines name match the string
                // if it does, add path to result
                if (containsString(lineName)) pathsArray.push(`${countryName}/${factoryName}/${lineName}`)
                line.products.forEach(product => {
                    if (containsString(product.productName)) {
                        pathsArray.push(`${countryName}/${factoryName}/${lineName}/${product.productName}`)
                    }
                })
            })
        })
    })

    return pathsArray
}
